import { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';

interface MilestoneProgressRingProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

export const MilestoneProgressRing = ({
  progress,
  size = 48,
  strokeWidth = 4,
  className,
}: MilestoneProgressRingProps) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const previousProgress = useRef(0);
  
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (animatedProgress / 100) * circumference;
  
  useEffect(() => {
    // Initial load animation - draw from 0
    if (isInitialLoad) {
      const timer = setTimeout(() => {
        setAnimatedProgress(progress);
        setIsInitialLoad(false);
        previousProgress.current = progress;
      }, 100);
      return () => clearTimeout(timer);
    }
    
    // Subsequent updates - smooth transition
    if (progress !== previousProgress.current) {
      setAnimatedProgress(progress);
      previousProgress.current = progress;
    }
  }, [progress, isInitialLoad]);

  return (
    <div 
      className={cn(
        "relative flex items-center justify-center shrink-0",
        className
      )}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Gradient definition */}
        <defs>
          <linearGradient id={`progress-gradient-${size}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="hsl(var(--accent))" />
          </linearGradient>
        </defs>
        
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
          className="opacity-30"
        />
        
        {/* Progress ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#progress-gradient-${size})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cn(
            isInitialLoad 
              ? "transition-[stroke-dashoffset] duration-1000 ease-out" 
              : "transition-[stroke-dashoffset] duration-500 ease-out"
          )}
        />
      </svg>
      
      {/* Center percentage text */}
      <span 
        className={cn(
          "absolute text-foreground font-bold",
          size >= 48 ? "text-sm" : "text-xs"
        )}
      >
        {Math.round(animatedProgress)}%
      </span>
    </div>
  );
};
